import React, { useState } from "react";
import { IconButton, Tooltip, CircularProgress } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { toast } from "react-toastify";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { BASE_URL } from "../../global";
import axiosWithToken from "../../utils/components/axiosTokenConfig";
import {
  calFrequencyOptions,
  calMethodOptions,
  calPointOptions,
  ConOfDucdOptions,
  fixedColumns as defaultFixedColumns,
  locationOptions,
} from "../../constants/srfConstants";
import { paratype } from "../../constants/masterInstrumentConstants";

// Standard dropdown options
export const CONDITIONS_OF_DUC = ConOfDucdOptions.map((item) => item.label);
export const CAL_FREQUENCIES = calFrequencyOptions.map((item) => item.label);
export const CAL_POINTS = calPointOptions.map((item) => item.label);
export const CAL_METHODS = calMethodOptions.map((item) => item.label);
export const LOCATIONS = locationOptions.map(
  (item) => `${item.id}, ${item.label}`
);

// Column width map
export const COLUMN_WIDTH_MAP = {
  instrumentId: 35,
  materialType: 28,
  requestedDucName: 25,
  DUCID: 15,
  serialNumber: 16,
  ranges: 30,
  lc: 18,
  make: 15,
  model: 15,
  ConOfDuc: 18,
  calFrequency: 22,
  accuracy: 15,
  location: 15,
  locationOfInstrument: 22,
  calPoint: 20,
  calMethod: 28,
  department: 16,
  operatingRange: 30,
  sensorType: 16,
  sensorCoefficient: 18,
  mpe: 15,
};

export const getColumnWidth = (key) => {
  if (COLUMN_WIDTH_MAP[key]) return COLUMN_WIDTH_MAP[key];
  return Math.max(String(key || "").length + 8, 18);
};

// Get columns based on branch settings
export const resolveBranchColumns = ({
  fixedColumns = null,
  settings = {},
  extraColumns = null,
} = {}) => {
  const sourceColumns =
    Array.isArray(fixedColumns) && fixedColumns.length > 0
      ? fixedColumns
      : defaultFixedColumns;

  // Base columns without internal id
  const columns = sourceColumns
    .filter((col) => (col.key || col.field) !== "id")
    .map((col) => {
      const key = col.key || col.field;
      return {
        key,
        header: key,
        width: getColumnWidth(key),
      };
    });

  // Add branch extra columns if configured
  let extraKeys = [];
  if (Array.isArray(extraColumns) && extraColumns.length > 0) {
    extraKeys = extraColumns;
  } else if (settings?.srfInstrumentsAdditionalColumns?.value) {
    extraKeys = settings.srfInstrumentsAdditionalColumns.value
      .split(",")
      .map((k) => k.trim())
      .filter(Boolean);
  }

  extraKeys.forEach((extraKey) => {
    if (!columns.some((col) => col.key === extraKey)) {
      columns.push({
        key: extraKey,
        header: extraKey,
        width: getColumnWidth(extraKey),
      });
    }
  });

  return columns;
};

// Build 3 sample rows with base data only
export const generateDynamicSampleRows = ({
  materialsList = [],
  conditionsList = CONDITIONS_OF_DUC,
  frequenciesList = CAL_FREQUENCIES,
  locationsList = LOCATIONS,
  calPointsList = CAL_POINTS,
  columns = [],
} = {}) => {
  const defaultMat =
    materialsList.find((m) => m.toLowerCase().includes("steel")) ||
    materialsList[0] ||
    "0.0000115, steel";

  const cond1 = conditionsList[0] || "Satisfactory";
  const cond2 = conditionsList[2] || conditionsList[0] || "NA";
  const cond3 = conditionsList[3] || conditionsList[1] || "Ok";

  const freq1 =
    frequenciesList.find((f) => f.includes("3 Month")) || frequenciesList[2] || "3 Months";
  const freq2 =
    frequenciesList.find((f) => f.includes("24 Month")) || frequenciesList[8] || "24 Months";
  const freq3 =
    frequenciesList.find((f) => f.includes("3 Month")) || frequenciesList[2] || "3 Months";

  const loc1 =
    locationsList.find((l) => l.toLowerCase().includes("in house")) ||
    locationsList[0] ||
    "1, In House";
  const loc2 =
    locationsList.find((l) => l.toLowerCase().includes("lab")) ||
    locationsList[2] ||
    "3, Lab";
  const loc3 =
    locationsList.find((l) => l.toLowerCase().includes("onsite")) ||
    locationsList[1] ||
    "2, Onsite";

  const calPoint1 = calPointsList[0] || "As per procedure";
  const calPoint2 = calPointsList[0] || "As per procedure";
  const calPoint3 = calPointsList[1] || calPointsList[0] || "As per customer";

  // Sample rows data with new ranges, lc, accuracy and blank instrumentId, calMethod, sensorType
  const rawSample1 = {
    instrumentId: "",
    materialType: defaultMat,
    requestedDucName: "Micrometer",
    DUCID: "PL03",
    serialNumber: "33",
    ranges: "0 to 300mm",
    lc: "0.01 mm",
    make: "make",
    model: "model",
    ConOfDuc: cond1,
    calFrequency: freq1,
    accuracy: "0.01 mm",
    location: loc1,
    locationOfInstrument: "main line",
    calPoint: calPoint1,
    calMethod: "",
    department: "",
    operatingRange: "10mm to 15mm",
    sensorType: "",
    sensorCoefficient: "",
    mpe: "",
  };

  const rawSample2 = {
    instrumentId: "",
    materialType: defaultMat,
    requestedDucName: "",
    DUCID: "PL03",
    serialNumber: "",
    ranges: " 0 to 1000 rpm, 0 to 500 rpm ",
    lc: "0.1 rpm , 1 rpm",
    make: "make",
    model: "",
    ConOfDuc: cond2,
    calFrequency: freq2,
    accuracy: "0.1 rpm , 1 rpm",
    location: loc2,
    locationOfInstrument: "-",
    calPoint: calPoint2,
    calMethod: "",
    department: "",
    operatingRange: " 0 to 1000 rpm, 0 to 500 rpm ",
    sensorType: "",
    sensorCoefficient: "",
    mpe: "",
  };

  const rawSample3 = {
    instrumentId: "",
    materialType: defaultMat,
    requestedDucName: "",
    DUCID: "PLO4",
    serialNumber: "",
    ranges: "As per Range",
    lc: "As per Range",
    make: "make",
    model: "",
    ConOfDuc: cond3,
    calFrequency: freq3,
    accuracy: "As per Range",
    location: loc3,
    locationOfInstrument: "",
    calPoint: calPoint3,
    calMethod: "",
    department: "",
    operatingRange: "As per Range",
    sensorType: "",
    sensorCoefficient: "",
    mpe: "",
  };

  // Keep extra columns blank
  const activeKeys = columns.map((col) => col.key);

  const filterRowKeys = (rawRow) => {
    const row = {};
    activeKeys.forEach((key) => {
      row[key] = rawRow[key] !== undefined ? rawRow[key] : "";
    });
    return row;
  };

  return [
    filterRowKeys(rawSample1),
    filterRowKeys(rawSample2),
    filterRowKeys(rawSample3),
  ];
};

// Generate and download inward sample excel
export const downloadSampleExcel = async ({
  instruments = [],
  settings = {},
  fixedColumns = null,
  extraColumns = null,
  fileName = "Sample_DUC_Inward.xlsx",
} = {}) => {
  let instrumentsList = instruments;
  const branchSettings = { ...(settings || {}) };

  // Fetch instruments and settings together
  const fetchPromises = [];

  if (!instrumentsList || instrumentsList.length === 0) {
    fetchPromises.push(
      axiosWithToken
        .get(
          `${BASE_URL}instruments?_fields=id,instrumentName&_where=(status,eq,1)`
        )
        .then((res) => {
          instrumentsList = res.data || [];
        })
        .catch((err) => {
          console.error("Error fetching dynamic instruments for sample excel:", err);
          instrumentsList = [];
        })
    );
  }

  if (
    !branchSettings.srfInstrumentsAdditionalColumns ||
    !branchSettings.materialType ||
    !branchSettings.sensorType
  ) {
    fetchPromises.push(
      axiosWithToken
        .get(
          `${BASE_URL}settings?_where=((keyName,eq,srfInstrumentsAdditionalColumns)~or(keyName,eq,materialType)~or(keyName,eq,sensorType))~and(status,ne,-1)&_fields=id,keyName,value`
        )
        .then((setRes) => {
          if (Array.isArray(setRes.data)) {
            setRes.data.forEach((s) => {
              branchSettings[s.keyName] = s;
            });
          }
        })
        .catch((err) => {
          console.warn("Could not fetch specific settings by keyName:", err);
        })
    );
  }

  if (fetchPromises.length > 0) {
    await Promise.all(fetchPromises);
  }

  // Parse materials from server settings
  let materialsList = [];
  if (branchSettings?.materialType?.value) {
    materialsList = branchSettings.materialType.value
      .split(",")
      .map((opt) => {
        const trimmed = opt.trim();
        if (trimmed.includes(":")) {
          const [label, val] = trimmed.split(":");
          return `${val.trim()}, ${label.trim()}`;
        }
        return trimmed;
      })
      .filter(Boolean);
  }

  // Get sensor types from settings or paratypes
  let sensorTypesList = [];
  if (branchSettings?.sensorType?.value) {
    sensorTypesList = branchSettings.sensorType.value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  } else if (paratype?.Measure?.["Temperature Simulation"]) {
    sensorTypesList = paratype.Measure["Temperature Simulation"];
  }

  // Resolve columns for current branch
  const resolvedColumns = resolveBranchColumns({
    fixedColumns,
    settings: branchSettings,
    extraColumns,
  });

  // Generate 3 sample rows
  const sampleRows = generateDynamicSampleRows({
    materialsList,
    conditionsList: CONDITIONS_OF_DUC,
    frequenciesList: CAL_FREQUENCIES,
    locationsList: LOCATIONS,
    calPointsList: CAL_POINTS,
    columns: resolvedColumns,
  });

  // Create workbook
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "LabCity";
  workbook.lastModifiedBy = "LabCity";
  workbook.created = new Date();
  workbook.modified = new Date();

  // Sheet1 for data entry
  const sheet1 = workbook.addWorksheet("Sheet1", {
    views: [{ showGridLines: true }],
  });

  // Sheet2 for dropdown lookups
  const sheet2 = workbook.addWorksheet("Sheet2", {
    views: [{ showGridLines: true }],
  });
  sheet2.state = "hidden";

  // Sheet 2 dropdown lists
  // Col A: Instruments
  instrumentsList.forEach((inst, idx) => {
    sheet2.getCell(`A${idx + 1}`).value = `${inst.id}, ${inst.instrumentName}`;
  });

  // Col C: Material Type
  materialsList.forEach((item, idx) => {
    sheet2.getCell(`C${idx + 1}`).value = item;
  });

  // Col E: Condition of DUC
  CONDITIONS_OF_DUC.forEach((item, idx) => {
    sheet2.getCell(`E${idx + 1}`).value = item;
  });

  // Col G: Calibration frequency
  CAL_FREQUENCIES.forEach((item, idx) => {
    sheet2.getCell(`G${idx + 1}`).value = item;
  });

  // Col I: Calibration Point
  CAL_POINTS.forEach((item, idx) => {
    sheet2.getCell(`I${idx + 1}`).value = item;
  });

  // Col K: Calibration Method
  CAL_METHODS.forEach((item, idx) => {
    sheet2.getCell(`K${idx + 1}`).value = item;
  });

  // Col M: Sensor Type
  sensorTypesList.forEach((item, idx) => {
    sheet2.getCell(`M${idx + 1}`).value = item;
  });

  // Col O: Location
  LOCATIONS.forEach((item, idx) => {
    sheet2.getCell(`O${idx + 1}`).value = item;
  });

  // Set Sheet 1 columns with center layout
  sheet1.columns = resolvedColumns.map((col) => ({
    header: col.header,
    key: col.key,
    width: col.width,
    style: {
      alignment: { vertical: "middle", horizontal: "center" },
      protection: { locked: false },
    },
  }));

  // Header styling
  const headerRow = sheet1.getRow(1);
  headerRow.height = 28;
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF4472C4" },
    };
    cell.font = {
      name: "Calibri",
      size: 12,
      bold: true,
      color: { argb: "FFFFFFFF" },
    };
    cell.alignment = { vertical: "middle", horizontal: "center" };
    cell.border = {
      top: { style: "thin", color: { argb: "FFB2B2B2" } },
      left: { style: "thin", color: { argb: "FFB2B2B2" } },
      bottom: { style: "thin", color: { argb: "FFB2B2B2" } },
      right: { style: "thin", color: { argb: "FFB2B2B2" } },
    };
    cell.protection = { locked: true };
  });

  // Add the 3 sample rows with center layout
  sampleRows.forEach((sample) => {
    const row = sheet1.addRow(sample);
    row.eachCell((cell) => {
      cell.protection = { locked: false };
      cell.font = { name: "Calibri", size: 11 };
      cell.alignment = { vertical: "middle", horizontal: "center" };
    });
  });

  // Find column index by key
  const getColIndex = (key) => {
    const idx = resolvedColumns.findIndex((c) => c.key === key);
    return idx !== -1 ? idx + 1 : null;
  };

  // Convert index to column letter (A, B, C...)
  const toColLetter = (colIdx) => {
    let temp = colIdx;
    let letter = "";
    while (temp > 0) {
      const rem = (temp - 1) % 26;
      letter = String.fromCharCode(65 + rem) + letter;
      temp = Math.floor((temp - 1) / 26);
    }
    return letter;
  };

  // Add dropdown validations covering the entire column
  const instCol = getColIndex("instrumentId");
  if (instCol && instrumentsList.length > 0) {
    const letter = toColLetter(instCol);
    sheet1.dataValidations.add(`${letter}2:${letter}1048576`, {
      type: "list",
      allowBlank: true,
      formulae: [`Sheet2!$A$1:$A$${instrumentsList.length}`],
    });
  }

  const matCol = getColIndex("materialType");
  if (matCol && materialsList.length > 0) {
    const letter = toColLetter(matCol);
    sheet1.dataValidations.add(`${letter}2:${letter}1048576`, {
      type: "list",
      allowBlank: true,
      formulae: [`Sheet2!$C$1:$C$${materialsList.length}`],
    });
  }

  const conCol = getColIndex("ConOfDuc");
  if (conCol && CONDITIONS_OF_DUC.length > 0) {
    const letter = toColLetter(conCol);
    sheet1.dataValidations.add(`${letter}2:${letter}1048576`, {
      type: "list",
      allowBlank: true,
      formulae: [`Sheet2!$E$1:$E$${CONDITIONS_OF_DUC.length}`],
    });
  }

  const freqCol = getColIndex("calFrequency");
  if (freqCol && CAL_FREQUENCIES.length > 0) {
    const letter = toColLetter(freqCol);
    sheet1.dataValidations.add(`${letter}2:${letter}1048576`, {
      type: "list",
      allowBlank: true,
      formulae: [`Sheet2!$G$1:$G$${CAL_FREQUENCIES.length}`],
    });
  }

  const locCol = getColIndex("location");
  if (locCol && LOCATIONS.length > 0) {
    const letter = toColLetter(locCol);
    sheet1.dataValidations.add(`${letter}2:${letter}1048576`, {
      type: "list",
      allowBlank: true,
      formulae: [`Sheet2!$O$1:$O$${LOCATIONS.length}`],
    });
  }

  const calPointCol = getColIndex("calPoint");
  if (calPointCol && CAL_POINTS.length > 0) {
    const letter = toColLetter(calPointCol);
    sheet1.dataValidations.add(`${letter}2:${letter}1048576`, {
      type: "list",
      allowBlank: true,
      formulae: [`Sheet2!$I$1:$I$${CAL_POINTS.length}`],
    });
  }

  const calMethodCol = getColIndex("calMethod");
  if (calMethodCol && CAL_METHODS.length > 0) {
    const letter = toColLetter(calMethodCol);
    sheet1.dataValidations.add(`${letter}2:${letter}1048576`, {
      type: "list",
      allowBlank: true,
      formulae: [`Sheet2!$K$1:$K$${CAL_METHODS.length}`],
    });
  }

  const sensorTypeCol = getColIndex("sensorType");
  if (sensorTypeCol && sensorTypesList.length > 0) {
    const letter = toColLetter(sensorTypeCol);
    sheet1.dataValidations.add(`${letter}2:${letter}1048576`, {
      type: "list",
      allowBlank: true,
      formulae: [`Sheet2!$M$1:$M$${sensorTypesList.length}`],
    });
  }

  // Protect Sheet 1 with password bfi
  await sheet1.protect("BFI", {
    selectLockedCells: true,
    selectUnlockedCells: true,
    formatCells: true,
    formatColumns: true,
    formatRows: true,
    insertColumns: false,
    insertRows: true,
    insertHyperlinks: true,
    deleteColumns: false,
    deleteRows: true,
    sort: true,
    autoFilter: true,
    pivotTables: true,
  });

  // Protect Sheet 2 with password bfi
  await sheet2.protect("bfi", {
    selectLockedCells: false,
    selectUnlockedCells: false,
  });

  // Save and download file
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, fileName);
};

// Download icon button with hover tooltip
export const SampleExcelDownload = ({
  settings = {},
  fixedColumns = null,
  extraColumns = null,
  tooltipTitle = "Sample Excel file of inward",
  size = "small",
  color = "primary",
  sx = {},
}) => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await downloadSampleExcel({ settings, fixedColumns, extraColumns });
      setTimeout(() => {
        setDownloading(false);
      }, 500);
    } catch (err) {
      console.error("Failed to generate and download sample excel:", err);
      toast.error(
        "Failed to download Sample Excel: " + (err.message || "Unknown error")
      );
      setDownloading(false);
    }
  };

  return (
    <Tooltip title={downloading ? "Downloading..." : tooltipTitle} arrow placement="top">
      <span>
        <IconButton
          id="srfslist-sample-excel-btn"
          size={size}
          color={color}
          disabled={downloading}
          onClick={handleDownload}
          sx={{
            ...sx,
          }}
        >
          {downloading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <FileDownloadIcon fontSize={size === "small" ? "small" : "medium"} />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default SampleExcelDownload;
